/**
 * UC-T9 org billing analysis — pure transforms (legacy BillingAnalysisWidget parity).
 */

export interface OrgBillingRecord {
  year: number;
  month: number;
  resource_department: string;
  resource_technology: string;
  /** When present in source JSON (grade billing / extended exports). */
  resource_grade?: string;
  /** When present in source JSON (project allocation). */
  resource_project?: string;
  /** Project billing / UC-T7: stable id (display uses resource_project name only). */
  project_id?: number;
  /** Project billing: e.g. client | internal (from gold_fact). */
  project_category?: string;
  /** UC-T8 / jsonbyAM: account manager (owner) display name. */
  resource_account_owner?: string;
  /** UC-T8: client label (from project–client mapping). */
  client_name?: string;
  /** UC-T8: project manager display name from timesheet row. */
  project_manager?: string;
  user_id: number;
  full_name: string;
  user_is_active: number;
  billable_hours: number | string;
  non_billable_hours: number | string;
  total_hours: number | string;
  total_leaves_days: number | string;
}

export interface OrgBillingPayload {
  records: OrgBillingRecord[];
  years_available: number[];
  year_months_available?: string[];
}

export type PeriodType = 'fy_india' | 'calendar';

export interface PeriodFilterState {
  periodType: PeriodType;
  fyYear: string;
  fyQuarter: string;
  tsYear: string;
  tsMonth: string;
  tsYearEnd: string;
  tsMonthEnd: string;
}

export function parseNum(v: unknown): number {
  if (typeof v === 'string') return parseFloat(v) || 0;
  return Number(v) || 0;
}

/** UI: cap visible length; append … when longer. */
export function truncateWithEllipsis(s: string, maxChars: number): string {
  if (s.length <= maxChars) return s;
  return `${s.slice(0, maxChars)}...`;
}

export function normalizeOrgBillingJson(raw: unknown): OrgBillingPayload {
  const o = raw as Record<string, unknown>;
  const recs = Array.isArray(o.records) ? (o.records as OrgBillingRecord[]) : [];
  const ya = Array.isArray(o.years_available)
    ? (o.years_available as number[]).map((y) => Number(y)).filter((y) => !isNaN(y))
    : [];
  return {
    records: recs,
    years_available: ya,
    year_months_available: Array.isArray(o.year_months_available)
      ? (o.year_months_available as string[])
      : undefined,
  };
}

/** Indian FY start years present in data (Apr–Mar). */
export function fyStartYearsFromRecords(records: OrgBillingRecord[]): number[] {
  const years = new Set<number>();
  records.forEach((r) => {
    const y = Number(r.year);
    const m = Number(r.month) || 1;
    if (m >= 4) years.add(y);
    else years.add(y - 1);
  });
  return [...years].filter((y) => !isNaN(y)).sort((a, b) => b - a);
}

export function filterByDate(
  list: OrgBillingRecord[],
  f: PeriodFilterState,
): OrgBillingRecord[] {
  if (!list.length) return list;
  const { periodType, tsYear, tsMonth, tsYearEnd, tsMonthEnd, fyYear, fyQuarter } =
    f;
  let out = list;
  if (periodType === 'calendar') {
    if (tsYear !== 'all' && tsMonth !== 'all') {
      const startYm = Number(tsYear) * 12 + Number(tsMonth);
      if (tsYearEnd !== 'all' && tsMonthEnd !== 'all') {
        const endYm = Number(tsYearEnd) * 12 + Number(tsMonthEnd);
        out = out.filter((r) => {
          const yr = Number(r.year);
          const mr = Number(r.month) || 1;
          const ymr = yr * 12 + mr;
          return ymr >= startYm && ymr <= endYm;
        });
      } else {
        out = out.filter((r) => {
          const yr = Number(r.year);
          const mr = Number(r.month) || 1;
          return (
            yr === Number(tsYear) &&
            (tsMonth === 'all' || mr === Number(tsMonth))
          );
        });
      }
    } else {
      if (tsYear !== 'all')
        out = out.filter((r) => Number(r.year) === Number(tsYear));
      if (tsMonth !== 'all')
        out = out.filter((r) => Number(r.month) === Number(tsMonth));
    }
    return out;
  }
  if (periodType === 'fy_india' && fyYear !== 'all') {
    const fyStart = Number(fyYear);
    const fyEnd = fyStart + 1;
    return list.filter((r) => {
      const y = Number(r.year);
      const m = Number(r.month) || 1;
      const inFy = (m >= 4 && y === fyStart) || (m <= 3 && y === fyEnd);
      if (!inFy) return false;
      if (fyQuarter === 'all') return true;
      if (fyQuarter === 'Q1') return m >= 4 && m <= 6;
      if (fyQuarter === 'Q2') return m >= 7 && m <= 9;
      if (fyQuarter === 'Q3') return m >= 10 && m <= 12;
      if (fyQuarter === 'Q4') return m >= 1 && m <= 3;
      return true;
    });
  }
  return out;
}

export function filterByDeptTech(
  list: OrgBillingRecord[],
  department: string,
  technology: string,
): OrgBillingRecord[] {
  let out = list;
  if (department !== 'all')
    out = out.filter(
      (r) => (r.resource_department || '').trim() === department,
    );
  if (technology !== 'all')
    out = out.filter(
      (r) => (r.resource_technology || '').trim() === technology,
    );
  return out;
}

/** Empty `selected` = no filter. Keys must match the same normalisation as unique* helpers. */
export function filterBySelectedDimension(
  list: OrgBillingRecord[],
  selected: string[],
  getKey: (r: OrgBillingRecord) => string,
): OrgBillingRecord[] {
  if (!selected.length) return list;
  const set = new Set(selected.map((t) => t.trim()));
  return list.filter((r) => set.has(getKey(r)));
}

/** Empty `selected` = no filter (all technologies). Keys match {@link uniqueTechnologies}. */
export function filterBySelectedTechnologies(
  list: OrgBillingRecord[],
  selected: string[],
): OrgBillingRecord[] {
  return filterBySelectedDimension(
    list,
    selected,
    (r) => (r.resource_technology || '').trim() || '—',
  );
}

export interface BillingTotals {
  billable: number;
  nonBillable: number;
  total: number;
  leaveDays: number;
  productivity: number;
  resourceCount: number;
}

export function computeTotals(records: OrgBillingRecord[]): BillingTotals {
  let billable = 0;
  let nonBillable = 0;
  let total = 0;
  const userIds = new Set<number | string>();
  const leaveByUserMonth: Record<string, number> = {};
  records.forEach((r) => {
    billable += parseNum(r.billable_hours);
    nonBillable += parseNum(r.non_billable_hours);
    total += parseNum(r.total_hours);
    if (r.user_id != null) userIds.add(r.user_id);
    const ym = `${r.year}-${String(r.month || 0).padStart(2, '0')}`;
    const key = `${r.user_id}::${ym}`;
    const ld = parseNum(r.total_leaves_days);
    if (!leaveByUserMonth[key] || ld > leaveByUserMonth[key])
      leaveByUserMonth[key] = ld;
  });
  const leaveDays = Object.values(leaveByUserMonth).reduce((s, v) => s + v, 0);
  const productivity = total > 0 ? (billable / total) * 100 : 0;
  return {
    billable,
    nonBillable,
    total,
    leaveDays,
    productivity,
    resourceCount: userIds.size,
  };
}

/** Per YYYY-MM bucket under a resource (legacy BillingAnalysisWidget). */
export interface ResourceMonthBucket {
  billable: number;
  nonBillable: number;
  total: number;
  leaveDays: number;
}

export interface ResourceAgg {
  name: string;
  billable: number;
  nonBillable: number;
  total: number;
  leaveDays: number;
  isActive: boolean;
  /** Monthly breakdown for drill-down; keys sorted as `YYYY-MM`. */
  byMonth: Record<string, ResourceMonthBucket>;
}

/** Resource row when rolled up under technology (primary dept = max hours). */
export interface ResourceAggWithPrimaryDept extends ResourceAgg {
  primaryDepartment: string;
  /** Project drill: technologies for this resource on the slice, by hours desc. */
  technologiesRanked?: string[];
}

/** UC-T8 account-owner drill: projects under one AM. */
export interface AccountOwnerProjectDrillNode {
  displayName: string;
  projectId?: number;
  /** `Client · PM` (or single part) when data exists. */
  clientAndPmSubtitle?: string;
  resources: Record<string, ResourceAggWithPrimaryDept>;
}

/** UC-T8: Account owner → project → resource → month. */
export interface AccountOwnerDrillRootNode {
  departmentSubtitle: string;
  projects: Record<string, AccountOwnerProjectDrillNode>;
}

/** Technology-first drill tree: departments listed as subtitle on the tech row. */
export interface TechDrillNode {
  /** Sorted unique departments contributing to this technology. */
  departmentSubtitle: string;
  /** Project drill: technologies on this project / slice, by hours desc. */
  sliceTechnologiesRanked?: string[];
  /** Project drill: set when all rows in slice share one project_id. */
  sliceProjectId?: number;
  /** Project drill: category subtitle (client / internal, …). */
  sliceCategorySubtitle?: string;
  resources: Record<string, ResourceAggWithPrimaryDept>;
}

export interface DrillDownGroupOptions {
  /**
   * Track subtitle token (e.g. technology) by hours; expose ranked arrays;
   * omit joined departmentSubtitle (project-wise tab).
   */
  rankedSubtitleTokens?: boolean;
}

function sortKeysByHours(hours: Record<string, number>): string[] {
  return Object.entries(hours)
    .sort((a, b) => b[1] - a[1])
    .map(([k]) => k);
}

function formatProjectCategoryLabel(raw: string): string {
  const s = (raw || '').trim();
  if (!s || s === '—') return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export interface DrillDownNode {
  technologies: Record<
    string,
    {
      resources: Record<string, ResourceAgg>;
    }
  >;
}

export function buildDrillDown(
  records: OrgBillingRecord[],
): Record<string, DrillDownNode> {
  const byDept: Record<string, DrillDownNode> = {};
  records.forEach((r) => {
    const dept = (r.resource_department || '').trim() || '—';
    const tech = (r.resource_technology || '').trim() || '—';
    const uid = String(r.user_id);
    const name = (r.full_name || `User ${r.user_id}`).trim();
    const bh = parseNum(r.billable_hours);
    const nbh = parseNum(r.non_billable_hours);
    const th = parseNum(r.total_hours);
    const year = r.year;
    const month = r.month;
    if (!byDept[dept]) byDept[dept] = { technologies: {} };
    if (!byDept[dept].technologies[tech])
      byDept[dept].technologies[tech] = { resources: {} };
    const techObj = byDept[dept].technologies[tech];
    const isActive = r.user_is_active === 1;
    if (!techObj.resources[uid]) {
      techObj.resources[uid] = {
        name,
        billable: 0,
        nonBillable: 0,
        total: 0,
        leaveDays: 0,
        isActive: false,
        byMonth: {},
      };
    }
    const res = techObj.resources[uid];
    if (isActive) res.isActive = true;
    res.billable += bh;
    res.nonBillable += nbh;
    res.total += th;
    const leaveDays = parseNum(r.total_leaves_days);
    const ymKey = `${year}-${String(month || 0).padStart(2, '0')}`;
    if (!res.byMonth[ymKey])
      res.byMonth[ymKey] = { billable: 0, nonBillable: 0, total: 0, leaveDays: 0 };
    res.byMonth[ymKey].billable += bh;
    res.byMonth[ymKey].nonBillable += nbh;
    res.byMonth[ymKey].total += th;
    res.byMonth[ymKey].leaveDays = Math.max(
      res.byMonth[ymKey].leaveDays || 0,
      leaveDays,
    );
    res.leaveDays = Object.values(res.byMonth).reduce(
      (s, m) => s + (m.leaveDays || 0),
      0,
    );
  });
  return byDept;
}

type InternalTechResource = ResourceAgg & {
  _deptHours: Record<string, number>;
  _subtitleTokenHours?: Record<string, number>;
};

function buildDrillDownGrouped(
  records: OrgBillingRecord[],
  getGroupKey: (r: OrgBillingRecord) => string,
  trackSubtitleToken: (r: OrgBillingRecord) => string,
  options?: DrillDownGroupOptions,
): Record<string, TechDrillNode> {
  const ranked = options?.rankedSubtitleTokens === true;
  const subtitleSets: Record<string, Set<string>> = {};
  const groupTokenHours: Record<string, Record<string, number>> = {};
  const groupProjectIds: Record<string, Set<number>> = {};
  const groupProjectCategories: Record<string, Set<string>> = {};
  const byGroup: Record<string, Record<string, InternalTechResource>> = {};

  records.forEach((r) => {
    const dept = (r.resource_department || '').trim() || '—';
    const group = getGroupKey(r);
    const uid = String(r.user_id);
    const name = (r.full_name || `User ${r.user_id}`).trim();
    const bh = parseNum(r.billable_hours);
    const nbh = parseNum(r.non_billable_hours);
    const th = parseNum(r.total_hours);
    const year = r.year;
    const month = r.month;
    const subToken = trackSubtitleToken(r);

    if (ranked) {
      if (!groupTokenHours[group]) groupTokenHours[group] = {};
      groupTokenHours[group][subToken] =
        (groupTokenHours[group][subToken] || 0) + th;
      const pid = r.project_id;
      if (pid != null && !Number.isNaN(Number(pid))) {
        if (!groupProjectIds[group]) groupProjectIds[group] = new Set();
        groupProjectIds[group].add(Number(pid));
      }
      const pcat = (r.project_category || '').trim() || '—';
      if (!groupProjectCategories[group]) groupProjectCategories[group] = new Set();
      groupProjectCategories[group].add(pcat);
    } else {
      if (!subtitleSets[group]) subtitleSets[group] = new Set();
      subtitleSets[group].add(subToken);
    }

    if (!byGroup[group]) byGroup[group] = {};
    if (!byGroup[group][uid]) {
      const base: InternalTechResource = {
        name,
        billable: 0,
        nonBillable: 0,
        total: 0,
        leaveDays: 0,
        isActive: false,
        byMonth: {},
        _deptHours: {},
      };
      if (ranked) base._subtitleTokenHours = {};
      byGroup[group][uid] = base;
    }
    const res = byGroup[group][uid];
    if (r.user_is_active === 1) res.isActive = true;
    res.billable += bh;
    res.nonBillable += nbh;
    res.total += th;
    res._deptHours[dept] = (res._deptHours[dept] || 0) + th;

    const leaveDays = parseNum(r.total_leaves_days);
    const ymKey = `${year}-${String(month || 0).padStart(2, '0')}`;
    if (!res.byMonth[ymKey])
      res.byMonth[ymKey] = { billable: 0, nonBillable: 0, total: 0, leaveDays: 0 };
    res.byMonth[ymKey].billable += bh;
    res.byMonth[ymKey].nonBillable += nbh;
    res.byMonth[ymKey].total += th;
    res.byMonth[ymKey].leaveDays = Math.max(
      res.byMonth[ymKey].leaveDays || 0,
      leaveDays,
    );
    res.leaveDays = Object.values(res.byMonth).reduce(
      (s, m) => s + (m.leaveDays || 0),
      0,
    );

    if (ranked && res._subtitleTokenHours) {
      res._subtitleTokenHours[subToken] =
        (res._subtitleTokenHours[subToken] || 0) + th;
    }
  });

  const out: Record<string, TechDrillNode> = {};
  Object.entries(byGroup).forEach(([group, uidMap]) => {
    const resources: Record<string, ResourceAggWithPrimaryDept> = {};
    Object.entries(uidMap).forEach(([uid, res]) => {
      const primaryDepartment =
        Object.entries(res._deptHours).sort((a, b) => b[1] - a[1])[0]?.[0] ||
        '—';
      const techRanked =
        ranked && res._subtitleTokenHours
          ? sortKeysByHours(res._subtitleTokenHours)
          : undefined;
      const { _deptHours: _d, _subtitleTokenHours: _s, ...aggRest } = res;
      resources[uid] = {
        ...aggRest,
        primaryDepartment,
        ...(techRanked?.length ? { technologiesRanked: techRanked } : {}),
      };
    });
    const subtitle = ranked
      ? ''
      : [...(subtitleSets[group] || new Set<string>())].sort().join(' · ');
    const sliceTechnologiesRanked = ranked
      ? sortKeysByHours(groupTokenHours[group] || {})
      : undefined;
    const idSet = ranked ? groupProjectIds[group] : undefined;
    const sliceProjectId =
      idSet && idSet.size === 1 ? [...idSet][0] : undefined;
    const catSet = ranked ? groupProjectCategories[group] : undefined;
    const categoryTokens =
      catSet && catSet.size
        ? [...catSet].filter((c) => c && c !== '—').sort()
        : [];
    const sliceCategorySubtitle =
      ranked && categoryTokens.length
        ? categoryTokens.map(formatProjectCategoryLabel).join(' · ')
        : undefined;
    out[group] = {
      departmentSubtitle: subtitle,
      ...(sliceTechnologiesRanked?.length
        ? { sliceTechnologiesRanked }
        : {}),
      ...(sliceProjectId != null ? { sliceProjectId } : {}),
      ...(sliceCategorySubtitle
        ? { sliceCategorySubtitle }
        : {}),
      resources,
    };
  });
  return out;
}

/**
 * Drill-down keyed by technology first; subtitle lists departments in the slice
 * for that tech.
 */
export function buildDrillDownByTechnology(
  records: OrgBillingRecord[],
): Record<string, TechDrillNode> {
  return buildDrillDownGrouped(
    records,
    (r) => (r.resource_technology || '').trim() || '—',
    (r) => (r.resource_department || '').trim() || '—',
  );
}

/** Drill-down keyed by grade; subtitle lists departments (same idea as tech row). */
export function buildDrillDownByGrade(
  records: OrgBillingRecord[],
): Record<string, TechDrillNode> {
  return buildDrillDownGrouped(
    records,
    (r) => (r.resource_grade || '').trim() || '—',
    (r) => (r.resource_department || '').trim() || '—',
  );
}

const AM_PROJ_SEP = '\x1e';

/** Stable key for project slice under an account owner (id + name). */
export function accountOwnerProjectSliceKey(r: OrgBillingRecord): string {
  const name = (r.resource_project || '').trim() || '—';
  const pid = r.project_id;
  if (pid != null && !Number.isNaN(Number(pid)))
    return `${pid}${AM_PROJ_SEP}${name}`;
  return `_${AM_PROJ_SEP}${name}`;
}

export function parseAccountOwnerProjectSliceKey(key: string): {
  projectId?: number;
  displayName: string;
} {
  const i = key.indexOf(AM_PROJ_SEP);
  if (i < 0) return { displayName: key || '—' };
  const idPart = key.slice(0, i);
  const displayName = key.slice(i + AM_PROJ_SEP.length) || '—';
  if (idPart === '_') return { displayName };
  const n = Number(idPart);
  return {
    projectId: Number.isNaN(n) ? undefined : n,
    displayName,
  };
}

/**
 * UC-T8: Account owner → project → resource → month (nested; not {@link TechDrillNode}).
 */
function formatClientPmSubtitle(
  client: string | undefined,
  pm: string | undefined,
): string | undefined {
  const c = (client || '').trim();
  const p = (pm || '').trim();
  if (c && p) return `${c} · ${p}`;
  if (c) return c;
  if (p) return p;
  return undefined;
}

export function buildDrillDownByAccountOwnerHierarchy(
  records: OrgBillingRecord[],
): Record<string, AccountOwnerDrillRootNode> {
  const deptByAm: Record<string, Set<string>> = {};
  const triple: Record<
    string,
    Record<string, Record<string, InternalTechResource>>
  > = {};
  const metaByAmProj: Record<
    string,
    Record<string, { client?: string; pm?: string }>
  > = {};

  records.forEach((r) => {
    const am = (r.resource_account_owner || '').trim() || '—';
    const projKey = accountOwnerProjectSliceKey(r);
    const dept = (r.resource_department || '').trim() || '—';
    const cn = (r.client_name || '').trim();
    const pmm = (r.project_manager || '').trim();
    if (cn || pmm) {
      if (!metaByAmProj[am]) metaByAmProj[am] = {};
      if (!metaByAmProj[am][projKey]) metaByAmProj[am][projKey] = {};
      const slot = metaByAmProj[am][projKey];
      if (cn && !slot.client) slot.client = cn;
      if (pmm && !slot.pm) slot.pm = pmm;
    }
    const uid = String(r.user_id);
    const name = (r.full_name || `User ${r.user_id}`).trim();
    const bh = parseNum(r.billable_hours);
    const nbh = parseNum(r.non_billable_hours);
    const th = parseNum(r.total_hours);
    const year = r.year;
    const month = r.month;

    if (!deptByAm[am]) deptByAm[am] = new Set();
    deptByAm[am].add(dept);

    if (!triple[am]) triple[am] = {};
    if (!triple[am][projKey]) triple[am][projKey] = {};
    if (!triple[am][projKey][uid]) {
      triple[am][projKey][uid] = {
        name,
        billable: 0,
        nonBillable: 0,
        total: 0,
        leaveDays: 0,
        isActive: false,
        byMonth: {},
        _deptHours: {},
      };
    }
    const res = triple[am][projKey][uid];
    if (r.user_is_active === 1) res.isActive = true;
    res.billable += bh;
    res.nonBillable += nbh;
    res.total += th;
    res._deptHours[dept] = (res._deptHours[dept] || 0) + th;

    const leaveDays = parseNum(r.total_leaves_days);
    const ymKey = `${year}-${String(month || 0).padStart(2, '0')}`;
    if (!res.byMonth[ymKey])
      res.byMonth[ymKey] = { billable: 0, nonBillable: 0, total: 0, leaveDays: 0 };
    res.byMonth[ymKey].billable += bh;
    res.byMonth[ymKey].nonBillable += nbh;
    res.byMonth[ymKey].total += th;
    res.byMonth[ymKey].leaveDays = Math.max(
      res.byMonth[ymKey].leaveDays || 0,
      leaveDays,
    );
    res.leaveDays = Object.values(res.byMonth).reduce(
      (s, m) => s + (m.leaveDays || 0),
      0,
    );
  });

  const out: Record<string, AccountOwnerDrillRootNode> = {};
  Object.entries(triple).forEach(([am, projMap]) => {
    const projects: Record<string, AccountOwnerProjectDrillNode> = {};
    Object.entries(projMap).forEach(([projKey, uidMap]) => {
      const { projectId, displayName } = parseAccountOwnerProjectSliceKey(projKey);
      const resources: Record<string, ResourceAggWithPrimaryDept> = {};
      Object.entries(uidMap).forEach(([uid, raw]) => {
        const primaryDepartment =
          Object.entries(raw._deptHours).sort((a, b) => b[1] - a[1])[0]?.[0] ||
          '—';
        const { _deptHours: _d, _subtitleTokenHours: _s, ...aggRest } = raw;
        resources[uid] = {
          ...aggRest,
          primaryDepartment,
        };
      });
      const meta = metaByAmProj[am]?.[projKey];
      const clientAndPmSubtitle = formatClientPmSubtitle(
        meta?.client,
        meta?.pm,
      );
      projects[projKey] = {
        displayName,
        ...(projectId != null ? { projectId } : {}),
        ...(clientAndPmSubtitle
          ? { clientAndPmSubtitle }
          : {}),
        resources,
      };
    });
    const subtitle = [...(deptByAm[am] || new Set<string>())].sort().join(' · ');
    out[am] = { departmentSubtitle: subtitle, projects };
  });
  return out;
}

export function rollupAccountOwnerAmNode(node: AccountOwnerDrillRootNode): {
  billable: number;
  nonBillable: number;
  total: number;
  leaveDays: number;
  resourceCount: number;
  projectCount: number;
  productivity: number;
} {
  const uidSet = new Set<string>();
  let billable = 0;
  let nonBillable = 0;
  let total = 0;
  let leaveDays = 0;
  Object.values(node.projects).forEach((p) => {
    Object.entries(p.resources).forEach(([uid, r]) => {
      uidSet.add(uid);
      billable += r.billable;
      nonBillable += r.nonBillable;
      total += r.total;
      leaveDays += r.leaveDays || 0;
    });
  });
  const productivity = total > 0 ? (billable / total) * 100 : 0;
  return {
    billable,
    nonBillable,
    total,
    leaveDays,
    resourceCount: uidSet.size,
    projectCount: Object.keys(node.projects || {}).length,
    productivity,
  };
}

export function sortAccountOwnerAmEntries(
  entries: [string, AccountOwnerDrillRootNode][],
  sortKey: DrillSortKey,
  sortDir: 'asc' | 'desc',
): [string, AccountOwnerDrillRootNode][] {
  const mult = sortDir === 'asc' ? 1 : -1;
  const getVal = (
    am: string,
    node: AccountOwnerDrillRootNode,
  ): string | number => {
    const r = rollupAccountOwnerAmNode(node);
    if (sortKey === 'name') return am;
    if (sortKey === 'billable') return r.billable;
    if (sortKey === 'nonBillable') return r.nonBillable;
    if (sortKey === 'total') return r.total;
    if (sortKey === 'productivity') return r.productivity;
    if (sortKey === 'resources') return r.projectCount;
    if (sortKey === 'leaveDays') return r.leaveDays;
    return r.billable;
  };
  return [...entries].sort((a, b) => {
    const valA = getVal(a[0], a[1]);
    const valB = getVal(b[0], b[1]);
    if (sortKey === 'name')
      return mult * String(valA).localeCompare(String(valB));
    return mult * ((valA as number) - (valB as number));
  });
}

export function sortAccountOwnerProjectEntries(
  entries: [string, AccountOwnerProjectDrillNode][],
  sortKey: DrillSortKey,
  sortDir: 'asc' | 'desc',
): [string, AccountOwnerProjectDrillNode][] {
  const mult = sortDir === 'asc' ? 1 : -1;
  const getVal = (
    projKey: string,
    p: AccountOwnerProjectDrillNode,
  ): string | number => {
    const resList = Object.values(p.resources || {});
    const billable = resList.reduce((s, x) => s + x.billable, 0);
    const total = resList.reduce((s, x) => s + x.total, 0);
    const nonBillable = total - billable;
    const productivity = total > 0 ? (billable / total) * 100 : 0;
    const resourceCount = resList.length;
    const leaveDays = resList.reduce((s, x) => s + (x.leaveDays || 0), 0);
    const name = p.displayName || parseAccountOwnerProjectSliceKey(projKey).displayName;
    if (sortKey === 'name') return name;
    if (sortKey === 'billable') return billable;
    if (sortKey === 'nonBillable') return nonBillable;
    if (sortKey === 'total') return total;
    if (sortKey === 'productivity') return productivity;
    if (sortKey === 'resources') return resourceCount;
    if (sortKey === 'leaveDays') return leaveDays;
    return billable;
  };
  return [...entries].sort((a, b) => {
    const valA = getVal(a[0], a[1]);
    const valB = getVal(b[0], b[1]);
    if (sortKey === 'name')
      return mult * String(valA).localeCompare(String(valB));
    return mult * ((valA as number) - (valB as number));
  });
}

export function filterBySelectedGrades(
  list: OrgBillingRecord[],
  selected: string[],
): OrgBillingRecord[] {
  return filterBySelectedDimension(
    list,
    selected,
    (r) => (r.resource_grade || '').trim() || '—',
  );
}

export function filterBySelectedAccountOwners(
  list: OrgBillingRecord[],
  selected: string[],
): OrgBillingRecord[] {
  return filterBySelectedDimension(
    list,
    selected,
    (r) => (r.resource_account_owner || '').trim() || '—',
  );
}

export function filterBySelectedProjects(
  list: OrgBillingRecord[],
  selected: string[],
): OrgBillingRecord[] {
  return filterBySelectedDimension(
    list,
    selected,
    (r) => (r.resource_project || '').trim() || '—',
  );
}

/**
 * Project tab: keep rows whose `resource_project` appears with at least one
 * `resource_technology` in `selected`. Empty `selected` = no filter.
 */
export function filterProjectsBySelectedTechnologies(
  list: OrgBillingRecord[],
  selected: string[],
): OrgBillingRecord[] {
  if (!selected.length) return list;
  const techSet = new Set(selected.map((t) => t.trim()));
  const projectKeys = new Set<string>();
  list.forEach((r) => {
    const tech = (r.resource_technology || '').trim() || '—';
    if (!techSet.has(tech)) return;
    projectKeys.add((r.resource_project || '').trim() || '—');
  });
  return list.filter((r) =>
    projectKeys.has((r.resource_project || '').trim() || '—'),
  );
}

/** Drill-down keyed by project; technologies as ranked lists (UI column). */
export function buildDrillDownByProject(
  records: OrgBillingRecord[],
): Record<string, TechDrillNode> {
  return buildDrillDownGrouped(
    records,
    (r) => (r.resource_project || '').trim() || '—',
    (r) => (r.resource_technology || '').trim() || '—',
    { rankedSubtitleTokens: true },
  );
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** `2025-04` → `Apr 2025` (drill-down month rows). */
export function labelYearMonthKey(ymKey: string): string {
  const [ys, ms] = ymKey.split('-');
  const mo = parseInt(ms, 10);
  if (!ys || Number.isNaN(mo) || mo < 1 || mo > 12) return ymKey;
  return `${MONTH_NAMES[mo - 1]} ${ys}`;
}

/** Resource line under one month in month-first drill (project / account-owner). */
export interface DrillMonthResourceRow {
  uid: string;
  name: string;
  primaryDepartment: string;
  isActive: boolean;
  technologiesRanked?: string[];
  bucket: ResourceMonthBucket;
}

export function rollupMonthResourceRows(
  rows: DrillMonthResourceRow[],
): {
  billable: number;
  nonBillable: number;
  total: number;
  leaveDays: number;
  productivity: number;
  headcount: number;
} {
  let billable = 0;
  let nonBillable = 0;
  let total = 0;
  let leaveDays = 0;
  rows.forEach(({ bucket: m }) => {
    billable += m.billable;
    nonBillable += m.nonBillable;
    total += m.total;
    leaveDays += m.leaveDays || 0;
  });
  const productivity = total > 0 ? (billable / total) * 100 : 0;
  return {
    billable,
    nonBillable,
    total,
    leaveDays,
    productivity,
    headcount: rows.length,
  };
}

/**
 * Bucket resources by `YYYY-MM` for month-first drill. Empty key = totals when
 * no monthly breakdown exists.
 */
export function groupResourcesByMonth(
  resources: Record<string, ResourceAggWithPrimaryDept>,
): Record<string, DrillMonthResourceRow[]> {
  const byMonth: Record<string, DrillMonthResourceRow[]> = {};
  Object.entries(resources).forEach(([uid, r]) => {
    const byM = r.byMonth || {};
    const monthKeys = Object.keys(byM);
    if (monthKeys.length === 0) {
      if (r.total > 0 || r.billable > 0 || r.nonBillable > 0) {
        const ym = '';
        if (!byMonth[ym]) byMonth[ym] = [];
        byMonth[ym].push({
          uid,
          name: r.name,
          primaryDepartment: r.primaryDepartment,
          isActive: r.isActive,
          technologiesRanked: r.technologiesRanked,
          bucket: {
            billable: r.billable,
            nonBillable: r.nonBillable,
            total: r.total,
            leaveDays: r.leaveDays || 0,
          },
        });
      }
      return;
    }
    monthKeys.forEach((ym) => {
      const bucket = byM[ym];
      if (!bucket) return;
      if (!byMonth[ym]) byMonth[ym] = [];
      byMonth[ym].push({
        uid,
        name: r.name,
        primaryDepartment: r.primaryDepartment,
        isActive: r.isActive,
        technologiesRanked: r.technologiesRanked,
        bucket: {
          billable: bucket.billable,
          nonBillable: bucket.nonBillable,
          total: bucket.total,
          leaveDays: bucket.leaveDays || 0,
        },
      });
    });
  });
  return byMonth;
}

export function sortDrillMonthKeys(
  byMonth: Record<string, DrillMonthResourceRow[]>,
  sortKey: DrillSortKey,
  sortDir: 'asc' | 'desc',
): string[] {
  const keys = Object.keys(byMonth);
  const mult = sortDir === 'asc' ? 1 : -1;
  const rollup = (ym: string) =>
    rollupMonthResourceRows(byMonth[ym] || []);
  return [...keys].sort((a, b) => {
    if (sortKey === 'name') {
      if (a === '') return mult * 1;
      if (b === '') return mult * -1;
      return mult * a.localeCompare(b);
    }
    const ra = rollup(a);
    const rb = rollup(b);
    if (sortKey === 'billable') return mult * (ra.billable - rb.billable);
    if (sortKey === 'nonBillable')
      return mult * (ra.nonBillable - rb.nonBillable);
    if (sortKey === 'total') return mult * (ra.total - rb.total);
    if (sortKey === 'productivity')
      return mult * (ra.productivity - rb.productivity);
    if (sortKey === 'resources')
      return mult * (ra.headcount - rb.headcount);
    if (sortKey === 'leaveDays')
      return mult * (ra.leaveDays - rb.leaveDays);
    return mult * (ra.billable - rb.billable);
  });
}

export function sortDrillMonthResourceRows(
  rows: DrillMonthResourceRow[],
  sortKey: DrillSortKey,
  sortDir: 'asc' | 'desc',
): DrillMonthResourceRow[] {
  const mult = sortDir === 'asc' ? 1 : -1;
  const getVal = (row: DrillMonthResourceRow): string | number => {
    const b = row.bucket;
    const productivity = b.total > 0 ? (b.billable / b.total) * 100 : 0;
    if (sortKey === 'name') return row.name || row.uid;
    if (sortKey === 'billable') return b.billable;
    if (sortKey === 'nonBillable') return b.nonBillable;
    if (sortKey === 'total') return b.total;
    if (sortKey === 'productivity') return productivity;
    if (sortKey === 'resources') return 1;
    if (sortKey === 'leaveDays') return b.leaveDays || 0;
    return b.billable;
  };
  return [...rows].sort((a, b) => {
    const valA = getVal(a);
    const valB = getVal(b);
    if (sortKey === 'name')
      return mult * String(valA).localeCompare(String(valB));
    return mult * ((valA as number) - (valB as number));
  });
}

/** Flat rows for CSV export (one row per resource–month, or resource total if no months). */
export interface TimesheetDrillExportRow {
  Department: string;
  /** Technology, grade, or project name depending on active tab. */
  Slice: string;
  'User ID': string;
  Resource: string;
  Active: string;
  'Year-Month': string;
  Month: string;
  'Billing Hours': number;
  'Non-Billing Hours': number;
  'Total Hours': number;
  'Leave Days': number;
  'Productivity %': number;
}

export function buildTimesheetDrillDownExportRows(
  drillDown: Record<string, TechDrillNode>,
): TimesheetDrillExportRow[] {
  const rows: TimesheetDrillExportRow[] = [];
  Object.entries(drillDown).forEach(([slice, node]) => {
    Object.entries(node.resources || {}).forEach(([uid, res]) => {
      const monthKeys = Object.keys(res.byMonth || {}).sort();
      const push = (
        yearMonth: string,
        monthLabel: string,
        m: ResourceMonthBucket,
      ) => {
        const prod = m.total > 0 ? (m.billable / m.total) * 100 : 0;
        rows.push({
          Department: res.primaryDepartment,
          Slice: slice,
          'User ID': uid,
          Resource: res.name,
          Active: res.isActive ? 'Yes' : 'No',
          'Year-Month': yearMonth,
          Month: monthLabel,
          'Billing Hours': Math.round(m.billable * 100) / 100,
          'Non-Billing Hours': Math.round(m.nonBillable * 100) / 100,
          'Total Hours': Math.round(m.total * 100) / 100,
          'Leave Days': Math.round((m.leaveDays || 0) * 10) / 10,
          'Productivity %': Math.round(prod * 10) / 10,
        });
      };
      if (monthKeys.length === 0) {
        push('', '—', {
          billable: res.billable,
          nonBillable: res.nonBillable,
          total: res.total,
          leaveDays: res.leaveDays,
        });
      } else {
        monthKeys.forEach((ym) => {
          const bucket = res.byMonth[ym];
          if (bucket) push(ym, labelYearMonthKey(ym), bucket);
        });
      }
    });
  });
  rows.sort((a, b) => {
    const s = a.Slice.localeCompare(b.Slice);
    if (s !== 0) return s;
    const d = a.Department.localeCompare(b.Department);
    if (d !== 0) return d;
    const r = a.Resource.localeCompare(b.Resource);
    if (r !== 0) return r;
    return (a['Year-Month'] || '').localeCompare(b['Year-Month'] || '');
  });
  return rows;
}

/** CSV for project tab when drill is slice → month → resource. */
export function buildTimesheetProjectMonthFirstExportRows(
  drillDown: Record<string, TechDrillNode>,
): TimesheetDrillExportRow[] {
  const rows: TimesheetDrillExportRow[] = [];
  Object.entries(drillDown).forEach(([slice, node]) => {
    const byMonth = groupResourcesByMonth(node.resources || {});
    Object.keys(byMonth)
      .sort()
      .forEach((ym) => {
        byMonth[ym].forEach((row) => {
          const m = row.bucket;
          const prod = m.total > 0 ? (m.billable / m.total) * 100 : 0;
          rows.push({
            Department: row.primaryDepartment,
            Slice: slice,
            'User ID': row.uid,
            Resource: row.name,
            Active: row.isActive ? 'Yes' : 'No',
            'Year-Month': ym,
            Month: ym ? labelYearMonthKey(ym) : '—',
            'Billing Hours': Math.round(m.billable * 100) / 100,
            'Non-Billing Hours': Math.round(m.nonBillable * 100) / 100,
            'Total Hours': Math.round(m.total * 100) / 100,
            'Leave Days': Math.round((m.leaveDays || 0) * 10) / 10,
            'Productivity %': Math.round(prod * 10) / 10,
          });
        });
      });
  });
  rows.sort((a, b) => {
    const s = a.Slice.localeCompare(b.Slice);
    if (s !== 0) return s;
    const ym = (a['Year-Month'] || '').localeCompare(b['Year-Month'] || '');
    if (ym !== 0) return ym;
    return a.Resource.localeCompare(b.Resource);
  });
  return rows;
}

/** CSV rows for account-owner hierarchy (includes Project column). */
export interface TimesheetAccountOwnerDrillExportRow {
  'Account owner': string;
  Project: string;
  Department: string;
  'User ID': string;
  Resource: string;
  Active: string;
  'Year-Month': string;
  Month: string;
  'Billing Hours': number;
  'Non-Billing Hours': number;
  'Total Hours': number;
  'Leave Days': number;
  'Productivity %': number;
}

export function buildAccountOwnerHierarchyExportRows(
  tree: Record<string, AccountOwnerDrillRootNode>,
): TimesheetAccountOwnerDrillExportRow[] {
  const rows: TimesheetAccountOwnerDrillExportRow[] = [];
  Object.entries(tree).forEach(([am, node]) => {
    Object.entries(node.projects).forEach(([, p]) => {
      const projName = p.displayName || '—';
      const byMonth = groupResourcesByMonth(p.resources || {});
      Object.keys(byMonth)
        .sort()
        .forEach((ym) => {
          byMonth[ym].forEach((row) => {
            const m = row.bucket;
            const prod = m.total > 0 ? (m.billable / m.total) * 100 : 0;
            rows.push({
              'Account owner': am,
              Project: projName,
              Department: row.primaryDepartment,
              'User ID': row.uid,
              Resource: row.name,
              Active: row.isActive ? 'Yes' : 'No',
              'Year-Month': ym,
              Month: ym ? labelYearMonthKey(ym) : '—',
              'Billing Hours': Math.round(m.billable * 100) / 100,
              'Non-Billing Hours': Math.round(m.nonBillable * 100) / 100,
              'Total Hours': Math.round(m.total * 100) / 100,
              'Leave Days': Math.round((m.leaveDays || 0) * 10) / 10,
              'Productivity %': Math.round(prod * 10) / 10,
            });
          });
        });
    });
  });
  rows.sort((a, b) => {
    const o = a['Account owner'].localeCompare(b['Account owner']);
    if (o !== 0) return o;
    const p = a.Project.localeCompare(b.Project);
    if (p !== 0) return p;
    const ym = (a['Year-Month'] || '').localeCompare(b['Year-Month'] || '');
    if (ym !== 0) return ym;
    return a.Resource.localeCompare(b.Resource);
  });
  return rows;
}

export interface TechChartRow {
  name: string;
  billable: number;
  nonBillable: number;
  productivityPct: number;
}

export function buildChartRollup(
  records: OrgBillingRecord[],
  getKey: (r: OrgBillingRecord) => string,
): TechChartRow[] {
  const by: Record<string, { billable: number; nonBillable: number }> = {};
  records.forEach((r) => {
    const k = getKey(r);
    if (!by[k]) by[k] = { billable: 0, nonBillable: 0 };
    by[k].billable += parseNum(r.billable_hours);
    by[k].nonBillable += parseNum(r.non_billable_hours);
  });
  return Object.entries(by)
    .map(([name, v]) => {
      const total = v.billable + v.nonBillable;
      const productivityPct =
        total > 0 ? Math.round((v.billable / total) * 1000) / 10 : 0;
      return {
        name,
        billable: Math.round(v.billable * 10) / 10,
        nonBillable: Math.round(v.nonBillable * 10) / 10,
        productivityPct,
      };
    })
    .sort((a, b) => b.billable + b.nonBillable - (a.billable + a.nonBillable));
}

export function buildChartByTech(records: OrgBillingRecord[]): TechChartRow[] {
  return buildChartRollup(
    records,
    (r) => (r.resource_technology || '').trim() || '—',
  );
}

export function buildChartByGrade(records: OrgBillingRecord[]): TechChartRow[] {
  return buildChartRollup(
    records,
    (r) => (r.resource_grade || '').trim() || '—',
  );
}

export function buildChartByProject(
  records: OrgBillingRecord[],
): TechChartRow[] {
  return buildChartRollup(
    records,
    (r) => (r.resource_project || '').trim() || '—',
  );
}

export function buildChartByAccountOwner(
  records: OrgBillingRecord[],
): TechChartRow[] {
  return buildChartRollup(
    records,
    (r) => (r.resource_account_owner || '').trim() || '—',
  );
}

export interface MonthChartRow {
  key: string;
  label: string;
  billable: number;
  nonBillable: number;
  productivityPct: number;
}

export function buildChartByMonth(records: OrgBillingRecord[]): MonthChartRow[] {
  const byMonth: Record<
    string,
    { key: string; label: string; billable: number; nonBillable: number }
  > = {};
  records.forEach((r) => {
    const y = Number(r.year);
    const m = Number(r.month) || 1;
    const key = `${y}-${String(m).padStart(2, '0')}`;
    if (!byMonth[key])
      byMonth[key] = {
        key,
        label: `${MONTH_NAMES[m - 1]} ${y}`,
        billable: 0,
        nonBillable: 0,
      };
    byMonth[key].billable += parseNum(r.billable_hours);
    byMonth[key].nonBillable += parseNum(r.non_billable_hours);
  });
  return Object.values(byMonth)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((row) => {
      const total = row.billable + row.nonBillable;
      const productivityPct =
        total > 0 ? Math.round((row.billable / total) * 1000) / 10 : 0;
      return {
        ...row,
        billable: Math.round(row.billable * 100) / 100,
        nonBillable: Math.round(row.nonBillable * 100) / 100,
        productivityPct,
      };
    });
}

export function uniqueDepartments(records: OrgBillingRecord[]): string[] {
  const set = new Set<string>();
  records.forEach((r) => set.add((r.resource_department || '').trim() || '—'));
  return [...set].filter(Boolean).sort();
}

export function uniqueTechnologies(
  records: OrgBillingRecord[],
  department: string,
): string[] {
  let base = records;
  if (department !== 'all')
    base = base.filter(
      (r) => (r.resource_department || '').trim() === department,
    );
  const set = new Set<string>();
  base.forEach((r) => set.add((r.resource_technology || '').trim() || '—'));
  return [...set].filter(Boolean).sort();
}

export function uniqueGrades(records: OrgBillingRecord[]): string[] {
  const set = new Set<string>();
  records.forEach((r) => set.add((r.resource_grade || '').trim() || '—'));
  return [...set].filter(Boolean).sort();
}

export function uniqueProjects(records: OrgBillingRecord[]): string[] {
  const set = new Set<string>();
  records.forEach((r) => set.add((r.resource_project || '').trim() || '—'));
  return [...set].filter(Boolean).sort();
}

export function uniqueAccountOwners(records: OrgBillingRecord[]): string[] {
  const set = new Set<string>();
  records.forEach((r) =>
    set.add((r.resource_account_owner || '').trim() || '—'),
  );
  return [...set].filter(Boolean).sort();
}

export type DrillSortKey =
  | 'name'
  | 'billable'
  | 'nonBillable'
  | 'total'
  | 'productivity'
  | 'resources'
  | 'leaveDays';

function deptRollup(deptData: DrillDownNode): {
  billable: number;
  total: number;
  nonBillable: number;
  leaveDays: number;
  resourceCount: number;
} {
  let billable = 0;
  let total = 0;
  let leaveDays = 0;
  const ids = new Set<string>();
  Object.values(deptData.technologies || {}).forEach((t) => {
    Object.entries(t.resources || {}).forEach(([uid, r]) => {
      ids.add(uid);
      billable += r.billable;
      total += r.total;
      leaveDays += r.leaveDays || 0;
    });
  });
  return {
    billable,
    total,
    nonBillable: total - billable,
    leaveDays,
    resourceCount: ids.size,
  };
}

export function sortDeptEntries(
  entries: [string, DrillDownNode][],
  sortKey: DrillSortKey,
  sortDir: 'asc' | 'desc',
): [string, DrillDownNode][] {
  const mult = sortDir === 'asc' ? 1 : -1;
  const getVal = (dept: string, deptData: DrillDownNode): string | number => {
    const { billable, total, nonBillable, resourceCount, leaveDays } =
      deptRollup(deptData);
    const productivity = total > 0 ? (billable / total) * 100 : 0;
    if (sortKey === 'name') return dept;
    if (sortKey === 'billable') return billable;
    if (sortKey === 'nonBillable') return nonBillable;
    if (sortKey === 'total') return total;
    if (sortKey === 'productivity') return productivity;
    if (sortKey === 'resources') return resourceCount;
    if (sortKey === 'leaveDays') return leaveDays;
    return billable;
  };
  return [...entries].sort((a, b) => {
    const valA = getVal(a[0], a[1]);
    const valB = getVal(b[0], b[1]);
    if (sortKey === 'name')
      return mult * String(valA).localeCompare(String(valB));
    return mult * ((valA as number) - (valB as number));
  });
}

export function sortTechEntries(
  technologies: [string, { resources: Record<string, ResourceAgg> }][],
  sortKey: DrillSortKey,
  sortDir: 'asc' | 'desc',
): [string, { resources: Record<string, ResourceAgg> }][] {
  const mult = sortDir === 'asc' ? 1 : -1;
  const getVal = (
    tech: string,
    techData: { resources: Record<string, ResourceAgg> },
  ): string | number => {
    const resEntries = Object.values(techData.resources || {});
    const billable = resEntries.reduce((s, r) => s + r.billable, 0);
    const total = resEntries.reduce((s, r) => s + r.total, 0);
    const nonBillable = total - billable;
    const productivity = total > 0 ? (billable / total) * 100 : 0;
    const resourceCount = resEntries.length;
    const leaveDays = resEntries.reduce((s, r) => s + (r.leaveDays || 0), 0);
    if (sortKey === 'name') return tech;
    if (sortKey === 'billable') return billable;
    if (sortKey === 'nonBillable') return nonBillable;
    if (sortKey === 'total') return total;
    if (sortKey === 'productivity') return productivity;
    if (sortKey === 'resources') return resourceCount;
    if (sortKey === 'leaveDays') return leaveDays;
    return billable;
  };
  return [...technologies].sort((a, b) => {
    const valA = getVal(a[0], a[1]);
    const valB = getVal(b[0], b[1]);
    if (sortKey === 'name')
      return mult * String(valA).localeCompare(String(valB));
    return mult * ((valA as number) - (valB as number));
  });
}

export function sortResourceEntries<R extends ResourceAgg>(
  resources: [string, R][],
  sortKey: DrillSortKey,
  sortDir: 'asc' | 'desc',
): [string, R][] {
  const mult = sortDir === 'asc' ? 1 : -1;
  const getVal = (uid: string, r: R): string | number => {
    const productivity = r.total > 0 ? (r.billable / r.total) * 100 : 0;
    if (sortKey === 'name') return r.name || uid;
    if (sortKey === 'billable') return r.billable;
    if (sortKey === 'nonBillable') return r.nonBillable;
    if (sortKey === 'total') return r.total;
    if (sortKey === 'productivity') return productivity;
    if (sortKey === 'resources') return 1;
    if (sortKey === 'leaveDays') return r.leaveDays || 0;
    return r.billable;
  };
  return [...resources].sort((a, b) => {
    const valA = getVal(a[0], a[1]);
    const valB = getVal(b[0], b[1]);
    if (sortKey === 'name')
      return mult * String(valA).localeCompare(String(valB));
    return mult * ((valA as number) - (valB as number));
  });
}
